import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Maximize2, Bot, Send, MessageCircle } from 'lucide-react';
import { LiveChatPanel } from '../common/LiveChatWidget';
import api from '../../lib/api';

// Global controller: window.dispatchEvent(new CustomEvent('nuyed:drawer', { detail: { tab: 'chat' | 'bot', open: true } }))
export function openDrawer(tab = 'chat') { window.dispatchEvent(new CustomEvent('nuyed:drawer', { detail: { tab, open: true } })); }

function NuyBotPanel() {
  const [messages, setMessages] = useState([{ role: 'assistant', text: "Hi, I'm NuyBot. Ask me about the marketplace, escrow, resources or how to earn NUY." }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const sessionRef = useRef(null);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    setInput(''); setSending(true);
    try {
      const { data } = await api.post('/nuybot/chat', { message: text, session_id: sessionRef.current });
      sessionRef.current = data.session_id;
      setMessages((m) => [...m, { role: 'assistant', text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', text: 'I could not reach the server. Please try again.' }]);
    } finally { setSending(false); }
  };
  return (
    <div className="gx-chat-body" data-testid="gaxbot-panel">
      <div className="gx-chat-scroll">
        {messages.map((m, i) => (
          m.role === 'assistant' ? (
            <div key={i} className="gx-chat-msg">
              <span className="gx-chat-avatar grid place-items-center bg-gx-purple/20 text-gx-cyan"><Bot className="w-3.5 h-3.5" /></span>
              <div className="min-w-0 flex-1">
                <p className="gx-chat-meta"><span className="gx-chat-user">NuyBot</span><span className="gx-chat-role">AI</span></p>
                <p className="gx-chat-text whitespace-pre-wrap">{m.text}</p>
              </div>
            </div>
          ) : (
            <div key={i} className="flex justify-end"><div className="gx-bot-bubble-me">{m.text}</div></div>
          )
        ))}
        {sending && <div className="gx-chat-msg"><span className="gx-chat-avatar grid place-items-center bg-gx-purple/20 text-gx-cyan"><Bot className="w-3.5 h-3.5" /></span><span className="gx-loader mt-2"><span /><span /><span /></span></div>}
        <div ref={endRef} />
      </div>
      <div className="gx-chat-composer">
        <div className="gx-chat-inputrow">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Ask NuyBot… (Enter)" className="gx-chat-input" data-testid="gaxbot-input" />
          <button onClick={send} disabled={sending} className="gx-chat-send" aria-label="Send" data-testid="gaxbot-send"><Send className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

export default function ChatDrawer() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('chat');
  const [online, setOnline] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    const h = (e) => { const d = e.detail || {}; if (d.tab) setTab(d.tab); setOpen(d.open ?? true); };
    window.addEventListener('nuyed:drawer', h);
    return () => window.removeEventListener('nuyed:drawer', h);
  }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    document.body.classList.add('gx-drawer-open');
    return () => { document.removeEventListener('keydown', onKey); document.body.classList.remove('gx-drawer-open'); };
  }, [open]);
  const onOnline = useCallback((n) => setOnline(n), []);

  return (
    <>
      <div className={`gx-fab-stack ${open ? 'is-hidden' : ''}`}>
        <button onClick={() => { setTab('bot'); setOpen(true); }} className="gx-fab gx-fab-bot" title="NuyBot AI" data-testid="gaxbot-toggle"><Bot className="w-5 h-5" /></button>
        {pathname !== '/chat' && (
          <button onClick={() => { setTab('chat'); setOpen(true); }} className="gx-fab gx-fab-chat" title="Community chat" data-testid="floating-chat-toggle">
            <MessageCircle className="w-5 h-5" />
            {online > 0 && <span className="gx-fab-badge">{online}</span>}
          </button>
        )}
      </div>

      <div className={`gx-drawer-backdrop ${open ? 'is-open' : ''}`} onClick={() => setOpen(false)} />
      <aside className={`gx-drawer ${open ? 'is-open' : ''}`} aria-hidden={!open} data-testid="chat-drawer">
        <header className="gx-drawer-head">
          <div className="gx-drawer-tabs" role="tablist">
            <button role="tab" aria-selected={tab === 'chat'} onClick={() => setTab('chat')} className={`gx-drawer-tab ${tab === 'chat' ? 'is-active' : ''}`} data-testid="drawer-tab-chat">
              <span className={`gx-live-dot ${online ? '' : 'opacity-40'}`} /> NuyChat <span className="gx-drawer-online">{online} online</span>
            </button>
            <button role="tab" aria-selected={tab === 'bot'} onClick={() => setTab('bot')} className={`gx-drawer-tab ${tab === 'bot' ? 'is-active' : ''}`} data-testid="drawer-tab-bot">
              <Bot className="w-3.5 h-3.5" /> NuyBot <span className="gx-drawer-online">AI</span>
            </button>
          </div>
          <div className="flex items-center gap-1">
            {tab === 'chat' && <Link to="/chat" onClick={() => setOpen(false)} className="gx-drawer-iconbtn" title="Open full chat" data-testid="floating-chat-expand"><Maximize2 className="w-4 h-4" /></Link>}
            <button onClick={() => setOpen(false)} className="gx-drawer-iconbtn" title="Close" data-testid="floating-chat-close"><X className="w-[18px] h-[18px]" /></button>
          </div>
        </header>
        {open && (tab === 'chat' ? <LiveChatPanel onOnlineCount={onOnline} /> : <NuyBotPanel />)}
      </aside>
    </>
  );
}
